import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

import styles from "./Sidebar.module.css";
import SidebarNavLink from "./SidebarNavLink";
import SidebarCategory from "./SidebarCategory";
import { LayoutContext } from "./LayoutContext";
import { homeRoute, routes } from "../../routing/routes";
import { Privilege } from "../../../features/auth/Privilege";
import { selectAuthUser, selectProfile } from "../../../features/auth/authSlice";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useSelector(selectProfile);
  const authUser = useSelector(selectAuthUser);
  const { sidebarIsOpen, sidebarIsCollapsed, closeSidebar, toggleCollapsedSidebar } = useContext(LayoutContext);

  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  return (
    <div
      className={`
        ${styles.layoutSidebar}
        ${!sidebarIsOpen ? styles["layoutSidebar--closed"] : ""}
        ${sidebarIsCollapsed ? styles["layoutSidebar--collapsed"] : ""}
        flex flex-column h-full py-4 px-2 surface-section shadow-2 z-2
      `}
    >
      <div className="mb-4 gap-2 px-2">
        <Button
          className={`w-2rem h-2rem hidden md:flex align-items-center justify-content-center ${sidebarIsCollapsed ? "mx-auto" : ""}`}
          size="small"
          icon={sidebarIsCollapsed ? "pi pi-arrow-right" : "pi pi-arrow-left"}
          severity="secondary"
          text
          onClick={() => toggleCollapsedSidebar()}
        />
        {!sidebarIsCollapsed && (
          <img
            className="cursor-pointer"
            height={100}
            src="/images/logo.png"
            alt="Smart Suport Logo"
            onClick={() => navigate(homeRoute(authUser.privilege).path)}
          />
        )}
      </div>
      <SidebarCategory
        className="cursor-pointer"
        name={sidebarIsCollapsed ? "User" : "Portal utilizator"}
        requiredPrivilege={Privilege.User}
        onClick={() => navigate(routes.dashboard.path)}
      >
        <SidebarNavLink path={routes.dashboard.path} icon="pi-home" text="Tichete utilizator"></SidebarNavLink>
        <SidebarNavLink
          path={routes.createIncident.path}
          icon="pi pi-exclamation-triangle text-yellow-500"
          text="Inregistreaza incident"
        ></SidebarNavLink>
        <SidebarNavLink path={routes.createRequest.path} icon="pi pi-plus text-cyan-500" text="Creeaza cerere"></SidebarNavLink>
        <Divider />
      </SidebarCategory>
      <SidebarCategory
        className="cursor-pointer"
        name={sidebarIsCollapsed ? "Agent" : "Portal agent"}
        requiredPrivilege={Privilege.Agent}
        onClick={() => navigate(routes.agentTickets.path)}
      >
        <SidebarNavLink path={routes.agentTickets.path} icon="pi-wrench" text="Tichete agenti"></SidebarNavLink>
        <SidebarNavLink path={routes.questions.path} icon="pi-question-circle" text="Intrebari frecvente"></SidebarNavLink>
        <SidebarNavLink path={routes.myProfile.path} icon="pi-id-card" text="Contul meu"></SidebarNavLink>
        <SidebarNavLink path={routes.reports.path} icon="pi-chart-pie" text="Rapoarte"></SidebarNavLink>
        <SidebarNavLink path={routes.myFilters.path} icon="pi-filter" text="Filtrele mele"></SidebarNavLink>
        <SidebarNavLink path={routes.feedbacks.path} icon="pi-comments" text="Surveys"></SidebarNavLink>
        <Divider />
      </SidebarCategory>
      <SidebarCategory
        className="mb-4 cursor-pointer"
        name={sidebarIsCollapsed ? "Admin" : "Portal admin"}
        requiredPrivilege={Privilege.Admin}
        onClick={() => navigate(routes.departments.path)}
      >
        <SidebarNavLink path={routes.departments.path} icon="pi-users" text="Departamente"></SidebarNavLink>
        <SidebarNavLink path={routes.categories.path} icon="pi-book" text="Categorii"></SidebarNavLink>
        <SidebarNavLink path={routes.routingRules.path} icon="pi-directions" text="Reguli rutare"></SidebarNavLink>
        <SidebarNavLink path={routes.users.path} icon="pi-user" text="Utilizatori"></SidebarNavLink>
        <SidebarNavLink path={routes.teams.path} icon="pi-sitemap" text="Grupe"></SidebarNavLink>
        <SidebarNavLink path={routes.ticketStatuses.path} icon="pi-tags" text="Statusuri tichet"></SidebarNavLink>
        <SidebarNavLink path={routes.priorities.path} icon="pi-clock" text="Configurare SLA"></SidebarNavLink>
        <SidebarNavLink path={routes.emailTemplates.path} icon="pi-envelope" text="Sabloane email"></SidebarNavLink>
        <SidebarNavLink path={routes.workingHours.path} icon="pi-calendar" text="Timpi de operare"></SidebarNavLink>
        <SidebarNavLink path={routes.systemSetting.path} icon="pi-cog" text="Setari sistem"></SidebarNavLink>
        <SidebarNavLink path={routes.webhooks.path} icon="pi-bolt" text="Webhooks"></SidebarNavLink>
      </SidebarCategory>
      <Divider className="mt-auto" />
      <div style={{ minHeight: sidebarIsCollapsed ? "" : "60px" }} className="flex flex-column gap-4 overflow-hidden">
        {!sidebarIsCollapsed && <div className="text-center text-ellipsis">{profile?.displayName}</div>}
        <div
          className={`flex align-items-center justify-content-center gap-2 text-xl cursor-pointer`}
          onClick={() => navigate(routes.logout.path)}
        >
          <i className="pi pi-sign-out"></i>
          {!sidebarIsCollapsed ? <span>Logout</span> : <Tooltip target=".pi-sign-out" content="Logout" position="right" className="ml-2" />}
        </div>
      </div>
    </div>
  );
}
